import type { Post } from '../payload-types'

type Source = NonNullable<Post['sources']>[number]

/**
 * Le fonti dell'articolo, renderizzate in coda al corpo.
 *
 * Il campo `posts.sources` esisteva da sempre — raccolto dal generator, salvato dal worker,
 * persistito — e non compariva in nessun file di `src/app`. La tracciabilità che mancava al
 * lettore non richiedeva né una migrazione né una chiamata a pagamento: richiedeva di
 * renderizzare dati che erano già in tabella.
 *
 * VIVE FUORI DAL CORPO, e non è un dettaglio di layout: un LLM non può scrivere né alterare
 * questi elementi, e non passano da convertHTMLToLexical — che, verificato, scarta rel="nofollow"
 * dai link del corpo. Un blocco fonti scritto dentro l'articolo sarebbe l'ennesima cosa che il
 * modello può inventare; questo no.
 *
 * SE NON CI SONO FONTI IL COMPONENTE NON SPARISCE: lo dice. Un'assenza silenziosa è
 * indistinguibile da una pagina ben sorgentata, ed è esattamente la confusione che ha permesso a
 * 34 articoli senza una riga in posts_sources di sembrare uguali a uno documentato.
 */
export default function SourcesBlock({ sources }: { sources?: Source[] | null }) {
  const list = (sources ?? []).filter((s) => s?.url)

  if (!list.length) {
    return (
      <section className="mt-12 border-t border-zinc-100 pt-8" aria-labelledby="fonti">
        <h2 id="fonti" className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
          Fonti
        </h2>
        <p className="mt-3 text-sm text-zinc-500">Questa pagina non ha fonti registrate.</p>
      </section>
    )
  }

  return (
    <section className="mt-12 border-t border-zinc-100 pt-8" aria-labelledby="fonti">
      <h2 id="fonti" className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
        Fonti
      </h2>
      <ul className="mt-3 space-y-2 text-sm">
        {list.map((s, i) => (
          <li key={s.id ?? `${s.url}-${i}`} className="flex flex-wrap items-baseline gap-x-2">
            <a
              href={s.url as string}
              rel="nofollow noopener external"
              target="_blank"
              className="font-medium text-zinc-700 underline decoration-zinc-300 underline-offset-2 transition-colors hover:text-accent-strong"
            >
              {s.title || s.host || s.url}
            </a>
            {s.trustClass === 'istituzionale' ? (
              <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs font-medium text-zinc-600">fonte istituzionale</span>
            ) : null}
            {typeof s.factCount === 'number' && s.factCount > 0 ? (
              <span className="text-xs text-zinc-500">
                {s.factCount} {s.factCount === 1 ? 'dato' : 'dati'}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  )
}
